// src/components/modals/DeleteCustomerModal.jsx
import { useState } from 'react'
import { Button } from '../ui/Button'
import { X, Loader2, AlertTriangle, User, Trash2 } from 'lucide-react'
import CustomerService from '../../services/customerService'

const DeleteCustomerModal = ({ isOpen, onClose, customer, onCustomerDeleted }) => {
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  // Handle delete confirmation
  const handleDelete = async () => {
    if (!customer?._id) {
      setError('Customer ID is missing')
      return
    }

    try {
      setLoading(true)
      setError('')
      
      const response = await CustomerService.deleteCustomer(customer._id)
      
      if (response?.success) {
        console.log('✅ Customer deleted successfully:', customer._id)
        
        if (onCustomerDeleted) {
          onCustomerDeleted(customer)
        }
        
        onClose()
      } else {
        setError(response?.message || 'Failed to delete customer')
      }
    } catch (error) {
      console.error('❌ Error deleting customer:', error)
      
      if (error.response?.status === 404) {
        setError('Customer not found')
      } else if (error.response?.status === 409) {
        setError('Cannot delete customer. Customer may have associated orders or transactions.')
      } else {
        setError(error.message || 'Failed to delete customer')
      }
    } finally {
      setLoading(false)
    }
  }

  if (!isOpen || !customer) return null

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-md">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-2">
            <div className="p-2 bg-red-100 rounded-lg">
              <AlertTriangle className="h-5 w-5 text-red-600" />
            </div>
            <h2 className="text-xl font-semibold text-gray-900">Delete Customer</h2>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            disabled={loading}
          >
            <X className="h-5 w-5 text-gray-500" />
          </button>
        </div>

        {/* Warning Message */}
        <div className="mb-6">
          <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
            <div className="flex items-start space-x-3">
              <Trash2 className="h-5 w-5 text-red-600 mt-0.5 flex-shrink-0" />
              <div>
                <h3 className="text-sm font-medium text-red-800 mb-2">
                  This action cannot be undone
                </h3>
                <p className="text-sm text-red-700">
                  You are about to permanently delete this customer. This will remove all customer 
                  data from the system.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Customer Information */}
        <div className="mb-6 p-4 bg-gray-50 rounded-lg">
          <div className="flex items-center space-x-3">
            <User className="h-5 w-5 text-gray-500" />
            <div>
              <p className="font-medium text-gray-900">{customer.name}</p>
              <p className="text-sm text-gray-600">{customer.mobileNumber}</p>
              {customer.branch?.name && (
                <p className="text-sm text-gray-500">{customer.branch.name}</p>
              )}
            </div>
          </div>
        </div>

        {/* Additional Warning for Active Customers */}
        {customer.isActive && (customer.totalOrders > 0 || customer.loyaltyPoints > 0) && (
          <div className="mb-6">
            <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
              <div className="flex items-start space-x-3">
                <AlertTriangle className="h-5 w-5 text-yellow-600 mt-0.5 flex-shrink-0" />
                <div>
                  <h3 className="text-sm font-medium text-yellow-800 mb-1">
                    Active Customer Warning
                  </h3>
                  <p className="text-sm text-yellow-700">
                    This customer has {customer.totalOrders || 0} orders and{' '}
                    {customer.loyaltyPoints || 0} loyalty points. 
                    Deleting will permanently remove this data.
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Error Message */}
        {error && (
          <div className="mb-6 p-3 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-sm text-red-600">{error}</p>
          </div>
        )}

        {/* Confirmation Input */}
        <div className="mb-6">
          <p className="text-sm text-gray-700 mb-2">
            Type <span className="font-medium text-gray-900">DELETE</span> to confirm:
          </p>
          <input
            type="text"
            id="confirmDelete"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
            placeholder="Type DELETE to confirm"
            disabled={loading}
          />
        </div>

        {/* Actions */}
        <div className="flex space-x-3">
          <Button
            type="button"
            onClick={onClose}
            variant="outline"
            className="flex-1"
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={handleDelete}
            className="flex-1 bg-red-600 hover:bg-red-700 text-white"
            disabled={loading || document.getElementById('confirmDelete')?.value !== 'DELETE'}
          >
            {loading ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Deleting...
              </>
            ) : (
              <>
                <Trash2 className="h-4 w-4 mr-2" />
                Delete Customer
              </>
            )}
          </Button>
        </div>

        {/* Additional Info */}
        <div className="mt-4 text-xs text-gray-500 text-center">
          <p>Customer ID: {customer._id}</p>
        </div>
      </div>
    </div>
  )
}

export default DeleteCustomerModal